import { C } from '../data/constants.js';

export class Brick {
  constructor(col, row, type) {
    this.col = col;
    this.row = row;
    this.type = type;
    this.hp = C.BRICK_HP[type];
    this.maxHp = this.hp;
    this.alive = true;
    this.points = C.BRICK_POINTS[type];
    this.color = C.BRICK_COLORS[type];
    this.flashTimer = 0;  // white flash on hit

    // Calculate pixel position
    this.x = C.BRICK_OFFSET_X + col * (C.BRICK_W + C.BRICK_GAP);
    this.y = C.BRICK_OFFSET_Y + row * (C.BRICK_H + C.BRICK_GAP);
    this.w = C.BRICK_W;
    this.h = C.BRICK_H;

    // --- Special brick state ---

    // Regen: timer counts up, heals 1 HP when it reaches threshold
    this.regenTimer = 0;
    this.regenInterval = 4000; // ms — regenerate 1 HP every 4 seconds

    // Phase: blinks in/out, not collidable when phased out
    this.phaseTimer = 0;
    this.phaseCycle = 2000;    // ms — full on/off cycle
    this.phasedOut = false;    // true = invisible & non-collidable

    // TNT: pulsing glow animation
    this.tntPulse = Math.random() * Math.PI * 2;

    // Vortex: swirl animation
    this.vortexAngle = 0;
  }

  get centerX() { return this.x + this.w / 2; }
  get centerY() { return this.y + this.h / 2; }

  // Whether this brick can be collided with right now
  get collidable() {
    if (!this.alive) return false;
    if (this.phasedOut) return false;
    return true;
  }

  hit(damage = 1) {
    if (this.type === C.BRICK_INDESTRUCTIBLE) return false;
    if (this.phasedOut) return false;

    this.hp -= damage;
    this.flashTimer = 3;  // flash for 3 frames

    // Reset regen timer on hit (must be quick to kill regen bricks)
    this.regenTimer = 0;

    if (this.hp <= 0) {
      this.alive = false;
      return true;  // destroyed
    }
    return false;  // damaged but alive
  }

  update(dtMs) {
    if (!this.alive) return;

    // --- Regen brick: heal over time ---
    if (this.type === C.BRICK_REGEN && this.hp < this.maxHp) {
      this.regenTimer += dtMs;
      if (this.regenTimer >= this.regenInterval) {
        this.regenTimer = 0;
        this.hp = Math.min(this.hp + 1, this.maxHp);
      }
    }

    // --- Phase brick: blink in/out ---
    if (this.type === C.BRICK_PHASE) {
      this.phaseTimer += dtMs;
      // Phased out for the second half of each cycle
      this.phasedOut = (this.phaseTimer % this.phaseCycle) > (this.phaseCycle / 2);
    }

    // --- TNT pulse animation ---
    if (this.type === C.BRICK_TNT) {
      this.tntPulse += 0.05;
    }

    // --- Vortex swirl animation ---
    if (this.type === C.BRICK_VORTEX) {
      this.vortexAngle += 0.03;
    }
  }

  render(ctx) {
    if (!this.alive) return;

    // Phase brick: render with transparency when about to phase out
    if (this.type === C.BRICK_PHASE) {
      if (this.phasedOut) {
        // Ghost outline only
        ctx.strokeStyle = this.color;
        ctx.globalAlpha = 0.15;
        ctx.lineWidth = 1;
        ctx.strokeRect(this.x, this.y, this.w, this.h);
        ctx.globalAlpha = 1;
        return;
      }
      // Fade effect near phase transition
      const cyclePos = (this.phaseTimer % this.phaseCycle) / this.phaseCycle;
      if (cyclePos > 0.35 && cyclePos < 0.5) {
        ctx.globalAlpha = 1 - ((cyclePos - 0.35) / 0.15);
      }
    }

    // Flash white on hit
    if (this.flashTimer > 0) {
      ctx.fillStyle = '#ffffff';
      this.flashTimer--;
    } else {
      ctx.fillStyle = this.color;
    }

    // Glow effect
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 6;

    // TNT: pulsing glow
    if (this.type === C.BRICK_TNT) {
      const pulse = 0.5 + Math.sin(this.tntPulse) * 0.5;
      ctx.shadowBlur = 6 + pulse * 8;
    }

    ctx.fillRect(this.x, this.y, this.w, this.h);
    ctx.shadowBlur = 0;

    // Crack overlay for damaged multi-HP bricks
    if (this.maxHp > 1 && this.maxHp !== Infinity && this.hp < this.maxHp && this.hp > 0) {
      const crackAlpha = 1 - (this.hp / this.maxHp);
      ctx.strokeStyle = `rgba(0, 0, 0, ${crackAlpha * 0.8})`;
      ctx.lineWidth = 1;
      const cx = this.x + this.w / 2;
      const cy = this.y + this.h / 2;
      ctx.beginPath();
      ctx.moveTo(cx - 8, cy - 4);
      ctx.lineTo(cx, cy);
      ctx.lineTo(cx + 6, cy - 3);
      ctx.moveTo(cx - 3, cy + 2);
      ctx.lineTo(cx + 4, cy + 5);
      ctx.stroke();
    }

    // Inner highlight (top edge)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.fillRect(this.x + 1, this.y + 1, this.w - 2, 3);

    // --- Type-specific decorations ---

    // TNT: pulsing "!" icon
    if (this.type === C.BRICK_TNT) {
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('!', this.centerX, this.centerY);
    }

    // Indestructible: riveted metal look
    if (this.type === C.BRICK_INDESTRUCTIBLE) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.beginPath();
      ctx.arc(this.x + 5, this.y + this.h / 2, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(this.x + this.w - 5, this.y + this.h / 2, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Regen: heartbeat pulse + heal particles hint
    if (this.type === C.BRICK_REGEN) {
      if (this.hp < this.maxHp) {
        // Show regen progress bar
        const progress = this.regenTimer / this.regenInterval;
        ctx.fillStyle = 'rgba(0, 255, 100, 0.4)';
        ctx.fillRect(this.x, this.y + this.h - 2, this.w * progress, 2);
      }
      // "+" icon
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.font = 'bold 9px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('+', this.centerX, this.centerY);
    }

    // Vortex: swirl effect
    if (this.type === C.BRICK_VORTEX) {
      ctx.save();
      ctx.translate(this.centerX, this.centerY);
      ctx.rotate(this.vortexAngle);
      ctx.strokeStyle = 'rgba(170, 0, 255, 0.5)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(0, 0, 5, 0, Math.PI * 1.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, 3, Math.PI * 0.5, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Prism: rainbow shimmer
    if (this.type === C.BRICK_PRISM) {
      const shimmer = Date.now() * 0.003;
      const r = Math.sin(shimmer) * 127 + 128;
      const g = Math.sin(shimmer + 2) * 127 + 128;
      const b = Math.sin(shimmer + 4) * 127 + 128;
      ctx.fillStyle = `rgba(${r|0}, ${g|0}, ${b|0}, 0.25)`;
      ctx.fillRect(this.x, this.y, this.w, this.h);

      // Triangle icon
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(this.centerX, this.y + 3);
      ctx.lineTo(this.x + this.w - 6, this.y + this.h - 3);
      ctx.lineTo(this.x + 6, this.y + this.h - 3);
      ctx.closePath();
      ctx.stroke();
    }

    // Reset globalAlpha (Phase brick may have changed it)
    ctx.globalAlpha = 1;
  }
}
